import { useEffect, useRef } from 'react';
import { getSocket } from './socketHelpers';
import { isValidSocketEvent, isEventPaused, isEventEnabled } from './socketManager';

// React hook to register/unregister socket events declaratively
export default function useSocketEvent(socketOrRef, eventName, callback, deps = [], options = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const lastInvokeRef = useRef(0);

  useEffect(() => {
    const enabled = options.enabled === undefined ? true : options.enabled;
    const pauseCheck = options.pauseCheck === undefined ? true : options.pauseCheck;
    if (!enabled) return () => {};
    if (!isValidSocketEvent(eventName)) {
      console.warn('[useSocketEvent] Unknown socket event:', eventName);
      return () => {};
    }
    if (!isEventEnabled(eventName)) {
      console.debug(
        '[useSocketEvent] Listener registered for disabled event, skipping: ',
        eventName
      );
      return () => {};
    }

    const socket = getSocket(socketOrRef);
    if (!socket) return () => {};
    const listener = (...args) => {
      if (pauseCheck && isEventPaused(eventName)) return;
      if (typeof options.filter === 'function') {
        try {
          if (!options.filter(...args)) return;
        } catch (e) {
          console.error('[useSocketEvent] filter error', e);
        }
      }
      if (options.throttleMs) {
        const now = Date.now();
        if (now - lastInvokeRef.current < options.throttleMs) return;
        lastInvokeRef.current = now;
      }
      cbRef.current && cbRef.current(...args);
      if (typeof options.onNotification === 'function') {
        try {
          options.onNotification(...args);
        } catch (err) {
          console.error('[useSocketEvent] onNotification callback error', err);
        }
      }
      if (options.dispatchWindowEvent) {
        const customEventName = options.dispatchWindowEventName || eventName;
        try {
          window.dispatchEvent(new CustomEvent(customEventName, { detail: args[0] }));
        } catch (e) {
          // ignore
        }
      }
    };
    if (options.once && typeof socket.once === 'function') {
      socket.once(eventName, listener);
    } else if (typeof socket.on === 'function') {
      socket.on(eventName, listener);
    }
    return () => {
      if (socket && typeof socket.off === 'function') socket.off(eventName, listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getSocket(socketOrRef) ? getSocket(socketOrRef).id || getSocket(socketOrRef).connected : null,
    eventName,
    ...deps,
  ]);
}
